/* eslint-disable no-console */
import { Form, Input } from 'antd';
import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { Lock } from 'lucide-react';

import callApi from '../../Api/config';
import { toastError, toastSuccess } from '../../service/Toast';
import { CHANGE_PASSWORD, USERS_ENTITY } from '../../constants/EntityName';
import { REGEX_PASSWORD } from '../../utils/Regexp/Regex';
import { InvertFillButton } from '../../components/InvertFillButton';

import '../Login/styles.scss';

type FieldType = {
  username?: string;
  password?: string;
  remember?: string;
  confirmPassword?: string;
  newPassword?: string;
  currentPassword?: string;
};

const ChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { search } = history.location;
  const [form] = Form.useForm();

  const onFinish = async (values: any) => {
    const { currentPassword, newPassword, confirmPassword } = values;

    if (currentPassword === newPassword) {
      toastError('New password cannot be same as old password.');
      return;
    }

    if (confirmPassword !== newPassword) {
      return;
    }

    setLoading(true);
    const payload = {
      currentPassword,
      newPassword,
    };

    try {
      const response: any = await callApi(CHANGE_PASSWORD + search, 'PATCH', payload);
      if (response?.status) {
        toastSuccess(response?.data?.message);
        setLoading(false);
        history.push({
          pathname: USERS_ENTITY,
        });
      } else {
        toastError(response?.data?.message);
        setLoading(false);
      }
    } catch (error) {
      toastError('Invalid Password');
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="auth-page auth-page--standalone">
        <div className="auth-page__card">
          <div className="auth-page__header">
            <h2 className="auth-page__title">Change password</h2>
            <p className="auth-page__subtitle">Update your password to keep your account secure.</p>
          </div>

          <Form
            form={form}
            name="change_password"
            className="auth-form"
            layout="vertical"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<FieldType>
              label="Current password"
              name="currentPassword"
              className="auth-form__item"
              rules={[
                { required: true, message: 'Please enter your current password!' },
                { pattern: REGEX_PASSWORD, message: 'Please enter a valid password!' },
              ]}
            >
              <Input.Password
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="Enter your current password"
                prefix={<Lock size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
              />
            </Form.Item>

            <Form.Item<FieldType>
              label="New password"
              name="newPassword"
              className="auth-form__item"
              dependencies={['currentPassword']}
              rules={[
                { required: true, message: 'Please enter your new password!' },
                { pattern: REGEX_PASSWORD, message: 'Please enter a valid password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('currentPassword') !== value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('New password cannot be same as old password.')
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="Enter your new password"
                prefix={<Lock size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
              />
            </Form.Item>

            <Form.Item<FieldType>
              label="Re-enter new password"
              name="confirmPassword"
              className="auth-form__item"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please re-enter your new password!' },
                { pattern: REGEX_PASSWORD, message: 'Please enter a valid password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match.'));
                  },
                }),
              ]}
            >
              <Input.Password
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="Re-enter your new password"
                prefix={<Lock size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
              />
            </Form.Item>

            <div className="auth-form__submit-row">
              <Form.Item style={{ marginBottom: 0 }}>
                <InvertFillButton
                  variant="blue"
                  size="lg"
                  fontSize="lg"
                  fontWeight="semibold"
                  className="auth-form__submit-btn"
                  type="submit"
                  disabled={loading}
                >
                  <span>{loading ? 'Updating…' : 'Change password'}</span>
                </InvertFillButton>
              </Form.Item>
            </div>
          </Form>

          <div className="auth-page__footer">
            <span className="auth-page__footer-muted">
              Protected by your organization&apos;s security policies.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
